import type { CustomFieldDefinition } from "@repo/app-types/entities/customField";
import type { CustomFieldCondition } from "@repo/app-types/entities/filterConditions";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { type FC, useCallback, useMemo } from "react";
import { useAllDataExtractionFieldDefinitionsQuery } from "../../api/entities/dataExtractionField.js";
import { useAllUserCustomFieldDefinitionsQuery } from "../../api/entities/userCustomField.js";
import { formatCustomFieldCalendarDate } from "../../helpers/customFieldFormatting.js";
import { RemoveFilterChip } from "./filterChip.js";

export interface CustomFieldsFilterItemProps {
  value: readonly CustomFieldCondition[];
  onChange: (newValue: readonly CustomFieldCondition[]) => void;
}

export const CustomFieldsFilterItem: FC<CustomFieldsFilterItemProps> = ({
  value,
  onChange,
}) => {
  const { data: allUserCustomFieldDefinitions } =
    useAllUserCustomFieldDefinitionsQuery();
  const { data: allDataExtractionFieldDefinitions } =
    useAllDataExtractionFieldDefinitionsQuery();

  const allCustomFieldDefinitions = useMemo(
    () => [
      ...(allUserCustomFieldDefinitions ?? []),
      ...(allDataExtractionFieldDefinitions ?? []),
    ],
    [allUserCustomFieldDefinitions, allDataExtractionFieldDefinitions],
  );

  const handleRemove = useCallback(
    (condition: CustomFieldCondition) => {
      onChange(value.filter((c) => c !== condition));
    },
    [value, onChange],
  );

  return (
    <>
      {value.map((condition, index) => (
        <RemoveFilterChip
          key={`custom-field-condition-${index}`}
          onRemove={() => handleRemove(condition)}
        >
          <span className="max-w-32 truncate">
            {renderCustomFieldConditionSubject(
              allCustomFieldDefinitions,
              condition,
            )}
          </span>
          <span>&nbsp;{renderCustomFieldConditionValue(condition)}</span>
        </RemoveFilterChip>
      ))}
    </>
  );
};

export function renderCustomFieldConditionSubject(
  allCustomFieldDefinitions: CustomFieldDefinition[] | undefined,
  condition: CustomFieldCondition,
) {
  return (
    allCustomFieldDefinitions?.find(
      (field) => field.id === condition.customFieldDefinitionId,
    )?.display_name || "Unknown field"
  );
}

export function renderCustomFieldConditionValue(
  condition: CustomFieldCondition,
) {
  switch (condition.operator) {
    case "has_any_value":
      return "is not empty";

    case "after":
      return `is after ${formatCustomFieldCalendarDate(condition.value)}`;

    case "before":
      return `is before ${formatCustomFieldCalendarDate(condition.value)}`;

    case "within_selected_date_range":
      return "is within selected date range";

    case "=":
      if (typeof condition.value === "string") {
        return `= "${condition.value}"`;
      }

      if (typeof condition.value === "boolean") {
        return condition.value ? `= "Yes"` : `= "No"`;
      }

      return assertUnreachable(condition.value, () => "?");

    default:
      return assertUnreachable(condition, () => "?");
  }
}
